import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Crosshair, Compass } from "lucide-react";
import { Reveal, Eyebrow } from "../components/Reveal";
import { PATHWAY_STAGES } from "../data/content";

export const Pathway = () => {
  const [active, setActive] = useState(0);
  const stage = PATHWAY_STAGES[active];

  return (
    <section id="pathway" data-testid="translational-pathway-section" className="bg-[#070D1D] py-24 lg:py-36 relative overflow-hidden">
      <div className="absolute top-0 left-1/3 w-[500px] h-[400px] rounded-full bg-teal-500/[0.05] blur-[120px] pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6 sm:px-10 relative">
        <Reveal>
          <Eyebrow dark>The Translational Pathway</Eyebrow>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mt-6">
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.12] text-[#F8F9FA] max-w-2xl">
              One continuous strategy, from first insight to global scale.
            </h2>
            <p className="text-slate-400 text-base max-w-md leading-relaxed">
              Select a stage to see where programs most often stall — and where senior advisory
              changes the trajectory.
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.15} className="mt-16">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 border border-white/10 divide-x divide-y lg:divide-y-0 divide-white/10" data-testid="pathway-stage-selector">
            {PATHWAY_STAGES.map((s, i) => (
              <button
                key={s.num}
                data-testid={`pathway-stage-${s.name.toLowerCase()}`}
                onClick={() => setActive(i)}
                className={`relative text-left p-5 sm:p-6 transition-colors duration-300 group ${
                  active === i ? "bg-teal-950/40" : "hover:bg-white/[0.03]"
                }`}
              >
                <span className={`font-mono text-[11px] tracking-widest ${active === i ? "text-teal-400" : "text-slate-500"}`}>
                  {s.num}
                </span>
                <span className={`block mt-3 font-serif text-lg sm:text-xl leading-tight transition-colors duration-300 ${
                  active === i ? "text-teal-300" : "text-slate-300 group-hover:text-slate-100"
                }`}>
                  {s.name}
                </span>
                {active === i && (
                  <motion.span layoutId="pathway-indicator" className="absolute bottom-0 left-0 right-0 h-[2px] bg-teal-400" />
                )}
              </button>
            ))}
          </div>
        </Reveal>

        <AnimatePresence mode="wait">
          <motion.div
            key={active}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
            className="mt-12 grid lg:grid-cols-12 gap-10"
            data-testid="pathway-detail-panel"
          >
            <div className="lg:col-span-5">
              <p className="font-mono text-xs uppercase tracking-[0.3em] text-teal-400">
                Stage {stage.num} — {stage.tagline}
              </p>
              <p className="mt-6 text-slate-300 text-base sm:text-lg leading-relaxed">
                {stage.description}
              </p>
              {active < PATHWAY_STAGES.length - 1 && (
                <button
                  data-testid="pathway-next-stage"
                  onClick={() => setActive(active + 1)}
                  className="mt-8 inline-flex items-center gap-2 text-teal-300 hover:text-teal-200 text-sm font-medium tracking-wide transition-colors"
                >
                  Next: {PATHWAY_STAGES[active + 1].name}
                  <ArrowRight className="w-4 h-4" />
                </button>
              )}
            </div>
            <div className="lg:col-span-7 grid sm:grid-cols-2 gap-6">
              <div className="border border-white/10 p-6 sm:p-8 bg-white/[0.02]">
                <div className="flex items-center gap-3 text-slate-200">
                  <Crosshair className="w-4 h-4 text-teal-400" />
                  <h3 className="font-mono text-xs uppercase tracking-[0.25em]">Critical activities</h3>
                </div>
                <ul className="mt-6 space-y-3">
                  {stage.activities.map((a) => (
                    <li key={a} className="text-sm text-slate-400 leading-relaxed flex gap-3">
                      <span className="text-teal-500 mt-0.5">—</span>{a}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="border border-teal-500/25 p-6 sm:p-8 bg-teal-950/20">
                <div className="flex items-center gap-3 text-slate-200">
                  <Compass className="w-4 h-4 text-teal-400" />
                  <h3 className="font-mono text-xs uppercase tracking-[0.25em]">Where we intervene</h3>
                </div>
                <ul className="mt-6 space-y-3">
                  {stage.interventions.map((a) => (
                    <li key={a} className="text-sm text-teal-100/80 leading-relaxed flex gap-3">
                      <span className="text-teal-400 mt-0.5">—</span>{a}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
};
